import React, { useCallback, useState } from 'react';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { SetupTitle } from '../components/setup-page-title';
import OnboardingSetupFooterButtons from '../components/setup-footer-buttons';
import { CompanyNameAndPhoneQuestion } from '../components/company-name-and-phone-question';
import { LogService } from '../../../services/log-service';
import { useProgressiveDisclosureSegmentTracking } from '../hooks/use-progressive-disclosure-segment-tracking';

import { useSetupContext } from '../context';
import { updateCompanyNameAndPhone } from '../api/setup.api';
import { persistPhoneLastUpdatedUtc } from '../../settings/sign-in-and-security/sections/seller-communication/api';

import { COMPANY_PHONE, ONBOARDING_SETUP_EVENTS } from '../constants';
import messages from '../messages';

export const CompanyAndPhoneForm = () => {
  const { onContinue } = useSetupContext();
  const [companyName, setCompanyName] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [isSubmitting, setIsSubmitting] = useAsyncSafeState(false);
  const trackProgressiveDisclosure = useProgressiveDisclosureSegmentTracking(
    ONBOARDING_SETUP_EVENTS.PROGRESSIVE_DISCLOSURE_QUESTION_ANSWERED,
  );

  const onClickSkip = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onContinue();
      trackProgressiveDisclosure({
        question: COMPANY_PHONE,
        skip: true,
      });
    } catch (error) {
      LogService.logError(new Error('Error skipping company name and phone question', { cause: error }));
    }
    setIsSubmitting(false);
  }, [onContinue, setIsSubmitting, trackProgressiveDisclosure]);

  const onClickContinue = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await Promise.all([updateCompanyNameAndPhone(companyName, phoneNumber), onContinue()]);
      if (phoneNumber) {
        await persistPhoneLastUpdatedUtc();
      }
      trackProgressiveDisclosure({
        question: COMPANY_PHONE,
        skip: false,
      });
    } catch (error) {
      LogService.logError(new Error('Error saving company name and phone', { cause: error }));
    }
    setIsSubmitting(false);
  }, [setIsSubmitting, onContinue, trackProgressiveDisclosure, companyName, phoneNumber]);

  const onCompanyNameChange = useCallback((e) => {
    const {
      target: { value },
    } = e;
    setCompanyName(value);
  }, []);

  const onPhoneNumberChange = useCallback((e) => {
    const {
      target: { value },
    } = e;
    setPhoneNumber(value);
  }, []);

  return (
    <>
      <SetupTitle subtitleDescriptor={messages.companyAndPhoneQuestionSubtitle} titleDescriptor={messages.companyAndPhoneQuestionTitle} />
      <CompanyNameAndPhoneQuestion
        companyName={companyName}
        phoneNumber={phoneNumber}
        setCompanyName={onCompanyNameChange}
        setPhoneNumer={onPhoneNumberChange}
      />
      <OnboardingSetupFooterButtons
        disableContinueButton={(!companyName && !phoneNumber) || isSubmitting}
        disableSkipButton={isSubmitting}
        hideBackButton
        onContinue={onClickContinue}
        onSkip={onClickSkip}
        skipMessageDescriptor={messages.skip}
      />
    </>
  );
};
